import type { MapFieldStatus, MapRenderModel, MapRenderFieldLabelModel } from '@assets/core/map';
import type { MapApiLabel, MapApiResult } from './types';

/**
 * Maps the validated page API DTO into the visual-only contract owned by the shared renderer.
 *
 * Tooltip, dialog, legend, debt and camera action data deliberately remain in {@link MapApiResult}
 * so rendering does not become a page-level interaction or network boundary.
 *
 * @param mapData Validated complete `/api/v1/map` DTO owned by the `/map` presentation feature.
 * @returns New immutable renderer model containing only SVG canvas, layers and visual themes.
 * @throws {TypeError} When a field-label direction is outside the renderer visual contract.
 */
export function toMapRenderModel(mapData: MapApiResult): MapRenderModel {
  return {
    canvas: {
      width: mapData.canvas.width,
      height: mapData.canvas.height,
    },
    fields: mapData.fields.map((field) => ({
      id: field.id,
      status: field.paymentType,
      points: field.points.map((point) => ({ x: point.x, y: point.y })),
      label: toMapRenderFieldLabel(field.label),
    })),
    streets: mapData.streets.map((street) => ({
      id: street.id,
      displayName: street.displayName,
      centerX: street.centerX,
      centerY: street.centerY,
      rotationDegrees: street.rotationDegrees,
      fontSize: street.fontSize,
    })),
    cameras: mapData.cameras.map((camera) => ({
      mapPositionId: camera.mapPositionId,
      cameraId: camera.cameraId,
      positionX: camera.positionX,
      positionY: camera.positionY,
      rotationDegrees: camera.rotationDegrees,
      icon: {
        color: camera.icon.color,
        sizeMultiplier: camera.icon.sizeMultiplier,
      },
      label: {
        text: camera.label.text,
        color: camera.label.color,
        fontSize: camera.label.fontSize,
        offsetX: camera.label.offsetX,
        offsetY: camera.label.offsetY,
      },
    })),
    fieldStatusTheme: {
      GREEN: toFieldTheme({
        status: 'GREEN',
        mapData,
      }),
      YELLOW: toFieldTheme({
        status: 'YELLOW',
        mapData,
      }),
      RED: toFieldTheme({
        status: 'RED',
        mapData,
      }),
      WHITE: toFieldTheme({
        status: 'WHITE',
        mapData,
      }),
    },
    streetTheme: {
      textColor: mapData.mapStreetTheme.default.textColor,
    },
  };
}

/**
 * Converts a validated API label direction into the renderer's closed visual contract.
 *
 * @param label API label whose open string token must be narrowed before rendering.
 * @returns New renderer label model with a `HORIZONTAL` or `VERTICAL` direction.
 * @throws {TypeError} When the backend sends an unsupported label direction.
 */
function toMapRenderFieldLabel(label: MapApiLabel): MapRenderFieldLabelModel {
  if (label.direction !== 'HORIZONTAL' && label.direction !== 'VERTICAL') {
    throw new TypeError(`Unsupported map field label direction: ${label.direction}.`);
  }

  return {
    text: label.text,
    direction: label.direction,
    size: label.size,
    shiftX: label.shiftX,
    shiftY: label.shiftY,
  };
}

/**
 * Copies one field visual theme across the API-to-render-model boundary.
 *
 * @param params Requested field status and the validated page API DTO that owns its theme source.
 * @returns New renderer theme item detached from the API DTO.
 */
function toFieldTheme(params: {
  status: MapFieldStatus;
  mapData: MapApiResult;
}): MapRenderModel['fieldStatusTheme'][MapFieldStatus] {
  const theme = params.mapData.mapStatusTheme[params.status];

  return {
    fillColor: theme.fillColor,
    stroke: theme.stroke,
    textColor: theme.textColor,
  };
}
